import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Play, Stop, Trash, Voicemail } from "@phosphor-icons/react";

function formatWhen(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function formatDur(sec) {
  if (sec == null || Number.isNaN(Number(sec))) return "";
  const s = Math.max(0, Math.floor(Number(sec)));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

function formatCaller(n) {
  const s = String(n || "").trim();
  return s || "Unknown caller";
}

export default function AgentVoicemailInbox({ variant = "rail" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [playingId, setPlayingId] = useState("");
  const [busyId, setBusyId] = useState("");
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  const stopAudio = useCallback(() => {
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.removeAttribute("src");
      el.load();
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setPlayingId("");
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/agent/voicemail");
      setItems(data.items || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => () => stopAudio(), [stopAudio]);

  useEffect(() => {
    if (open) load();
    else stopAudio();
  }, [open, load, stopAudio]);

  const markHeard = (id) => {
    setItems((prev) => prev.map((m) => (
      m.id === id && !m.listened_at
        ? { ...m, listened_at: new Date().toISOString() }
        : m
    )));
  };

  const play = async (row) => {
    if (!row?.id) return;
    if (playingId === row.id) {
      stopAudio();
      return;
    }
    setBusyId(row.id);
    setError("");
    try {
      const { data } = await api.get(`/agent/voicemail/${row.id}/audio`, { responseType: "blob" });
      markHeard(row.id);
      stopAudio();
      const url = URL.createObjectURL(data);
      urlRef.current = url;
      const el = audioRef.current;
      if (!el) return;
      el.src = url;
      await el.play();
      setPlayingId(row.id);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e) || "Could not play voicemail");
    } finally {
      setBusyId("");
    }
  };

  const remove = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Delete voicemail from ${formatCaller(row.caller_number)}?`)) return;
    setBusyId(row.id);
    setError("");
    try {
      await api.delete(`/agent/voicemail/${row.id}`);
      if (playingId === row.id) stopAudio();
      setItems((prev) => prev.filter((m) => m.id !== row.id));
      toast.success("Voicemail deleted");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusyId("");
    }
  };

  const unread = items.filter((m) => !m.listened_at).length;

  const isHeader = variant === "header";
  const triggerCls = isHeader
    ? `relative p-1.5 rounded hover:bg-muted ${open ? "bg-muted text-foreground" : "text-muted-foreground"}`
    : (
      "relative flex flex-col items-center justify-center gap-1 w-full py-3 px-1 rounded-md border text-[11px] font-medium transition-colors "
      + (open
        ? "border-primary/30 bg-primary/10 text-primary"
        : "border-border bg-card hover:bg-muted text-foreground")
    );

  return (
    <>
      <button
        type="button"
        className={triggerCls}
        onClick={() => setOpen(true)}
        data-testid="agent-voicemail-btn"
        title="Listen to your voicemails"
      >
        <Voicemail className={isHeader ? "w-4 h-4" : "w-6 h-6"} weight={open || unread ? "fill" : "regular"} />
        {!isHeader ? <span>Voicemail</span> : null}
        {unread > 0 && (
          <span
            className={
              isHeader
                ? "absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 rounded-full bg-red-600 text-white text-[9px] font-bold leading-3.5 grid place-items-center"
                : "absolute top-1 right-1 min-w-[1.1rem] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] leading-4 text-center"
            }
            data-testid="agent-voicemail-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      <audio
        ref={audioRef}
        className="hidden"
        onEnded={stopAudio}
        data-testid="agent-voicemail-audio"
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="agent-voicemail-dialog">
          <DialogHeader>
            <DialogTitle>Voicemail</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[24rem] overflow-auto">
            {error && <p className="text-sm text-destructive" data-testid="agent-voicemail-error">{error}</p>}
            {loading && !items.length ? (
              <p className="text-sm text-muted-foreground">Loading voicemails…</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="agent-voicemail-empty">
                No voicemails in your mailbox.
              </p>
            ) : items.map((m) => (
              <div
                key={m.id}
                className="flex items-start gap-2 rounded-md border border-border p-2.5"
                data-testid="agent-voicemail-row"
                data-voicemail-id={m.id}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {formatCaller(m.caller_number)}
                    {!m.listened_at && (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide text-sky-700">New</span>
                    )}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {formatWhen(m.created_at)}
                    {m.duration_sec != null ? ` · ${formatDur(m.duration_sec)}` : ""}
                    {m.called_did ? ` · DID ${m.called_did}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  className="p-1.5 rounded hover:bg-muted text-sky-700 disabled:opacity-40"
                  title={playingId === m.id ? "Stop" : "Play"}
                  disabled={busyId === m.id || !m.has_audio}
                  onClick={() => play(m)}
                  data-testid="agent-voicemail-play"
                >
                  {playingId === m.id
                    ? <Stop className="w-4 h-4" weight="fill" />
                    : <Play className="w-4 h-4" weight="fill" />}
                </button>
                <button
                  type="button"
                  className="p-1.5 rounded hover:bg-destructive/10 text-destructive disabled:opacity-40"
                  title="Delete"
                  disabled={busyId === m.id}
                  onClick={() => remove(m)}
                  data-testid="agent-voicemail-delete"
                >
                  <Trash className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="agent-voicemail-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
